// Колоните на трите изгледа в „Сторно": списък, по сервитьор, по продукт.
//
// И трите работят в КЛИЕНТСКИ режим: `GET /bills/cancellations` връща целия
// период наведнъж (масив, без страниране), затова сортирането, филтрите и
// редът „Общо" се смятат в браузъра.

import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { parseDecimal } from "../../../../components/dataTable";
import type {
  ChecklistOption,
  ChipCellValue,
  TextCellValue,
} from "../../../../components/dataTable";
import { BatchBadge, BatchMarker } from "./BatchMarker";
import { batchPositionsOfRowModel } from "./cancellationsMath";
import type {
  NamedCancelledItem,
  ProductVoidRow,
  WaiterVoidRow,
} from "./cancellationsMath";

const listHelper = createColumnHelper<NamedCancelledItem>();
const waiterHelper = createColumnHelper<WaiterVoidRow>();
const productHelper = createColumnHelper<ProductVoidRow>();

type Names = {
  waiterName: (id: number | null | undefined) => string;
  itemName: (id: number) => string;
  stationName: (id: number) => string;
};

/* ─────────────────────────────── списък ─────────────────────────────── */

export const useCancellationsListColumns = (
  waiterOptions: ChecklistOption[],
  cancelledByOptions: ChecklistOption[],
  isMultiDay: boolean,
) => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      listHelper.accessor((row) => row.bill_name, {
        id: "bill_name",
        header: t("reportsPage.table"),
        size: 128,
        // Белезите на партидата живеят в тази клетка — затова редът е с ръчен
        // рендер, а не с декларативния `meta.cell`. Позицията се смята върху
        // ТЕКУЩИЯ редови модел (`getRowModel`), защото подредбата и филтърът
        // менят кой до кого стои — виж `batchPositionsOfRowModel`.
        cell: (context) => {
          const position =
            batchPositionsOfRowModel(context.table.getRowModel().rows).get(
              context.row.original.id,
            ) ?? "single";
          const batchSize = context.row.original.batch_size ?? 1;
          // Името на масата се изписва ВЕДНЪЖ за партидата: следващите ѝ редове
          // са продължение на същата сметка и стоят притъмнени, за да не се
          // четат като нови сметки. При „lone" няма съседи, значи няма и от
          // какво да е продължение — името остава плътно.
          const isContinuation = position === "middle" || position === "last";
          return (
            // `h-full` дърпа скобата до ръбовете на реда — иначе тя свършва с
            // текста и между два реда на партидата зее празно.
            <span className="flex h-full items-stretch gap-1.5">
              <BatchMarker position={position} size={batchSize} />
              <span className="flex min-w-0 flex-col justify-center gap-[3px]">
                <span
                  className={`truncate text-[13px] leading-[1.2] ${
                    isContinuation
                      ? "font-medium text-muted"
                      : "font-bold text-ink"
                  }`}
                >
                  {context.getValue()}
                </span>
                {position === "first" || position === "lone" ? (
                  <BatchBadge size={batchSize} />
                ) : null}
              </span>
            </span>
          );
        },
        meta: {
          alwaysVisible: true,
          filter: { kind: "text" },
          excel: { format: "text" },
        },
      }),

      listHelper.accessor((row) => row.cancelled_at, {
        id: "cancelled_at",
        header: t("reportsPage.voidsTime"),
        size: 104,
        meta: {
          cell: { kind: "dt", withDate: isMultiDay ? "always" : "never" },
          excel: { format: "date" },
        },
      }),

      listHelper.accessor((row): TextCellValue => ({
        primary: row.productName,
        secondary: row.reason ?? null,
      }), {
        id: "product",
        header: t("reportsPage.product"),
        size: 200,
        meta: {
          cell: { kind: "text", lines: 2 },
          filter: { kind: "text" },
          excel: { format: "text" },
        },
      }),

      listHelper.accessor((row) => row.waiterName, {
        id: "waiter",
        header: t("reportsPage.waiter"),
        size: 118,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "checklist", options: waiterOptions },
          excel: { format: "text" },
        },
      }),

      listHelper.accessor((row) => row.cancelledByName, {
        id: "cancelled_by",
        header: t("reportsPage.voidsCancelledBy"),
        size: 118,
        meta: {
          cell: { kind: "text" },
          // „Кой е натиснал" е половината от въпроса на управителя — без този
          // филтър се стига до него само с изнасяне в Excel.
          filter: { kind: "checklist", options: cancelledByOptions },
          hideBelow: "tablet",
          excel: { format: "text" },
        },
      }),

      // БЕЗ `decimals: 0` — теглените артикули идват с дробно количество и
      // закръглянето показваше 0.750 кг като „1".
      listHelper.accessor((row) => parseDecimal(row.quantity), {
        id: "quantity",
        header: t("reportsPage.qty"),
        size: 68,
        meta: {
          cell: { kind: "num" },
          total: "sum",
          excel: { format: "number" },
        },
      }),

      listHelper.accessor((row) => parseDecimal(row.price), {
        id: "price",
        header: t("reportsPage.unitPrice"),
        size: 104,
        meta: {
          cell: { kind: "money" },
          hideBelow: "tablet",
          excel: { format: "money" },
        },
      }),

      listHelper.accessor(
        (row): ChipCellValue | null =>
          row.is_defective
            ? { tone: "danger", label: t("reportsPage.voidsScrapShort") }
            : null,
        {
          id: "is_defective",
          header: t("reportsPage.voidsScrap"),
          size: 88,
          // Клетката е празна за обикновено сторно (бадж само на брака), а
          // празната стойност не може да бъде отметка в чеклист — затова
          // филтърът гледа самия ред, не показаната стойност.
          filterFn: (row, _columnId, filterValue) => {
            const selected = Array.isArray(filterValue)
              ? (filterValue as string[])
              : [];
            if (!selected.length) return true;
            return selected.includes(
              row.original.is_defective ? "defective" : "void",
            );
          },
          meta: {
            cell: { kind: "chip" },
            filter: {
              kind: "checklist",
              options: [
                { value: "defective", label: t("reportsPage.voidsScrapShort") },
                { value: "void", label: t("reportsPage.voidsPlain") },
              ],
            },
            hideBelow: "tablet",
          },
        },
      ),

      listHelper.accessor((row) => parseDecimal(row.total_sum), {
        id: "total_sum",
        header: t("reportsPage.amount"),
        size: 116,
        meta: {
          cell: { kind: "money", negative: true, strong: true },
          total: "sum",
          alwaysVisible: true,
          excel: { format: "money" },
        },
      }),
    ],
    [t, waiterOptions, cancelledByOptions, isMultiDay],
  );
};

/* ──────────────────────────── по сервитьор ──────────────────────────── */

export const useCancellationsWaiterColumns = (
  { waiterName }: Pick<Names, "waiterName">,
  showKitchen: boolean,
) => {
  const { t } = useTranslation();

  return useMemo(
    () =>
      [
        waiterHelper.accessor((row) => waiterName(row.waiterId), {
          id: "waiter",
          header: t("reportsPage.waiter"),
          size: 180,
          meta: {
            cell: { kind: "text" },
            alwaysVisible: true,
            excel: { format: "text" },
          },
        }),
        waiterHelper.accessor((row) => row.bills, {
          id: "bills",
          header: t("reportsPage.bills"),
          size: 96,
          meta: { cell: { kind: "num" }, total: "sum", excel: { format: "number" } },
        }),
        waiterHelper.accessor((row) => row.rows, {
          id: "rows",
          header: t("reportsPage.voidsRows"),
          size: 120,
          meta: { cell: { kind: "num" }, total: "sum", excel: { format: "number" } },
        }),
        ...(showKitchen
          ? [
              waiterHelper.accessor((row) => row.afterKitchen, {
                id: "after_kitchen",
                header: t("reportsPage.voidsAfterKitchen"),
                size: 120,
                meta: {
                  cell: { kind: "num" },
                  total: "sum",
                  excel: { format: "number" },
                },
              }),
            ]
          : []),
        waiterHelper.accessor((row) => row.total, {
          id: "total",
          header: t("reportsPage.voidsValue"),
          size: 128,
          meta: {
            cell: { kind: "money", negative: true, strong: true },
            total: "sum",
            alwaysVisible: true,
            excel: { format: "money" },
          },
        }),
        // `?? 0` тук показваше „0.0 %" там, където делът НЕ СЪЩЕСТВУВА (няма
        // закрит оборот на този сервитьор) — `null` се рисува с тире.
        waiterHelper.accessor((row) => row.shareOfRevenue, {
          id: "share",
          header: t("reportsPage.voidsShareOfRevenue"),
          size: 148,
          meta: { cell: { kind: "pct" }, excel: { format: "number" } },
        }),
      ],
    [t, waiterName, showKitchen],
  );
};

/* ───────────────────────────── по продукт ───────────────────────────── */

export const useCancellationsProductColumns = (
  { itemName, stationName }: Pick<Names, "itemName" | "stationName">,
  showKitchen: boolean,
) => {
  const { t } = useTranslation();

  return useMemo(
    () =>
      [
        productHelper.accessor((row) => itemName(row.itemId), {
          id: "product",
          header: t("reportsPage.product"),
          size: 240,
          meta: {
            cell: { kind: "text" },
            filter: { kind: "text" },
            alwaysVisible: true,
            excel: { format: "text" },
          },
        }),
        productHelper.accessor((row) => stationName(row.stationId), {
          id: "station",
          header: t("reportsPage.station"),
          size: 140,
          meta: {
            cell: { kind: "text" },
            filter: { kind: "checklist" },
            hideBelow: "tablet",
            excel: { format: "text" },
          },
        }),
        productHelper.accessor((row) => row.rows, {
          id: "rows",
          header: t("reportsPage.voidsRows"),
          size: 110,
          meta: { cell: { kind: "num" }, total: "sum", excel: { format: "number" } },
        }),
        productHelper.accessor((row) => row.quantity, {
          id: "quantity",
          header: t("reportsPage.qty"),
          size: 96,
          meta: { cell: { kind: "num" }, total: "sum", excel: { format: "number" } },
        }),
        ...(showKitchen
          ? [
              productHelper.accessor((row) => row.afterKitchen, {
                id: "after_kitchen",
                header: t("reportsPage.voidsAfterKitchen"),
                size: 120,
                meta: {
                  cell: { kind: "num" },
                  total: "sum",
                  excel: { format: "number" },
                },
              }),
            ]
          : []),
        productHelper.accessor((row) => row.total, {
          id: "total",
          header: t("reportsPage.voidsValue"),
          size: 128,
          meta: {
            cell: { kind: "money", negative: true, strong: true },
            total: "sum",
            alwaysVisible: true,
            excel: { format: "money" },
          },
        }),
      ],
    [t, itemName, stationName, showKitchen],
  );
};
